import { getReport, getReportStrategyDetail } from "../../../services/reportsApi";
import type { Backtest } from "../../../types/backtests";
import type { ReportStrategyPolicyVersion } from "../../../types/reports";
import { isBacktestSavedAsPolicyVersion } from "../Strategies/strategyDetailUtils";
import type { ComparisonRun } from "./simulatorComparisonTypes";

export function stampSavedPolicyVersionOnComparisonRun(
  run: ComparisonRun,
  policyVersions: ReportStrategyPolicyVersion[] | null | undefined,
): ComparisonRun {
  const backtestId = run.backtestId?.trim();
  if (!backtestId || run.isSavedStrategyVersion) return run;
  if (!isBacktestSavedAsPolicyVersion(backtestId, policyVersions)) return run;
  return { ...run, isSavedStrategyVersion: true };
}

export function stampSavedPolicyVersionOnComparisonRuns(
  runs: ComparisonRun[],
  policyVersions: ReportStrategyPolicyVersion[] | null | undefined,
): ComparisonRun[] {
  return runs.map((run) => stampSavedPolicyVersionOnComparisonRun(run, policyVersions));
}

export async function resolveStrategyIdForBacktest(
  backtest: Backtest | Record<string, unknown>,
): Promise<string | null> {
  const record = backtest as Record<string, unknown>;
  const fromRow =
    (typeof record.strategy_id === "string" && record.strategy_id.trim()) ||
    (typeof (backtest as Backtest).strategy_id === "string" && (backtest as Backtest).strategy_id?.trim()) ||
    "";
  if (fromRow) return fromRow;

  const reportId =
    (typeof record.report_id === "string" && record.report_id.trim()) ||
    (typeof (backtest as Backtest).report_id === "string" && (backtest as Backtest).report_id?.trim()) ||
    "";
  if (!reportId) return null;

  try {
    const report = await getReport(reportId);
    const strategyId = typeof report?.strategy_id === "string" ? report.strategy_id.trim() : "";
    return strategyId || null;
  } catch {
    return null;
  }
}

/** Hydrate `isSavedStrategyVersion` from strategy policy version rows (source_backtest_id). */
export async function stampRunsWithSavedPolicyVersionFlags(
  runs: ComparisonRun[],
  strategyId: string | null | undefined,
): Promise<ComparisonRun[]> {
  const sid = strategyId?.trim();
  if (!sid) return runs;
  if (!runs.some((run) => run.backtestId?.trim() && !run.isSavedStrategyVersion)) return runs;

  try {
    const detail = await getReportStrategyDetail(sid);
    return stampSavedPolicyVersionOnComparisonRuns(runs, detail?.policy_versions);
  } catch {
    return runs;
  }
}
